import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Button,
  Typography,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { DataGrid, GridColDef, useGridApiRef, GridRowParams, GridCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Sync as SyncIcon,
  Undo as UndoIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  UnfoldLess as CollapseAllIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useApiClient } from '../../config/axios';
import { useCustomer } from '../../contexts/CustomerContext';

// Types for our database-backed rows
interface DatabaseRow {
  id: number;
  config_id: string;
  json_path: string;
  path_order: string;
  node_type: 'object' | 'array' | 'value' | 'primitive';
  node_key: string | null;
  node_value: any;
  parent_path: string | null;
  depth: number;
  display_order: number;
  // UI state
  isExpanded?: boolean;
  isEditing?: boolean;
  _matchType?: string; // For search result sorting
}

interface DatabaseUIConfigGridProps {
  configId: string;
  onChange?: (changes: DatabaseRow[]) => void;
}

// Helper function to get display value based on node type
const getDisplayValue = (row: DatabaseRow, allRows: DatabaseRow[]): string => {
  if (row.node_type === 'object') {
    const childCount = allRows.filter(r => r.parent_path === row.json_path).length;
    return `{${childCount} properties}`;
  }
  if (row.node_type === 'array') {
    const childCount = allRows.filter(r => r.parent_path === row.json_path).length;
    return `[${childCount} items]`;
  }
  if (row.node_value === null) return 'null';
  if (typeof row.node_value === 'boolean') return row.node_value ? 'true' : 'false';
  return String(row.node_value);
};

// Friendly label for the Path column. For array-element rows (json_path ends
// in [N]), look up a child row whose key is `name` (preferred) or `id` and
// return `<bareKey> · <value>` so e.g. `leftNavSections[1]` displays as
// `leftNavSections · MACRO TRENDS`. Non-array-element rows fall back to the
// usual stripped last-segment label.
const getFriendlyPathLabel = (row: DatabaseRow, allRows: DatabaseRow[]): string => {
  const lastSeg = row.json_path.split('.').pop() || row.json_path;
  const bareKey = lastSeg.replace(/\[\d+\]/g, '');
  const isArrayElement = /\[\d+\]$/.test(lastSeg);
  if (!isArrayElement) return bareKey || row.json_path;

  // Prefer the element's `name` child, fall back to `id`. The DB's
  // `node_key` column is empty in customer data; the key lives in the
  // last segment of json_path (e.g. `...leftNavSections[0].name`). Match
  // on that. Use direct parent_path so we don't pick up a grandchild.
  const findChildValue = (key: string): string | null => {
    const child = allRows.find(r => {
      if (r.parent_path !== row.json_path) return false;
      if (r.node_type !== 'primitive' && r.node_type !== 'value') return false;
      const childLastSeg = r.json_path.split('.').pop() || '';
      // Strip any trailing [N] just in case.
      const childKey = childLastSeg.replace(/\[\d+\]$/, '');
      return childKey === key;
    });
    if (!child) return null;
    const v = child.node_value;
    return typeof v === 'string' && v ? v : null;
  };
  const label = findChildValue('name') ?? findChildValue('id');
  return label ? `${bareKey} · ${label}` : bareKey || row.json_path;
};

// Helper to determine if a row should be shown based on parent expansion
const isRowVisible = (row: DatabaseRow, expandedPaths: Set<string>, allRows: DatabaseRow[]): boolean => {
  // Top level items (depth 0) are always visible
  if (row.depth === 0) return true;

  // For deeper items, check if their immediate parent is expanded
  if (!row.parent_path) {
    return row.depth === 1; // Level 1 items with no parent are visible
  }

  // Find the parent row to check if it's expanded
  const parentRow = allRows.find(r => r.json_path === row.parent_path);
  if (!parentRow) {
    return row.depth === 1; // If we can't find parent, show level 1 items
  }

  // Parent must be expandable and expanded for this row to be visible
  const isParentExpandable = parentRow.node_type === 'object' || parentRow.node_type === 'array';
  return isParentExpandable && expandedPaths.has(row.parent_path);
};

// Helper to parse path with bracket notation (e.g., "printSizes[0].width")
// Returns parsed segments with type information
const parsePath = (path: string): Array<{ key: string, isArrayIndex: boolean, index?: number }> => {
  if (!path) return [];

  const segments: Array<{ key: string, isArrayIndex: boolean, index?: number }> = [];
  const parts = path.split('.');

  parts.forEach(part => {
    // Check if this part has bracket notation like "printSizes[0]"
    const bracketMatch = part.match(/^(.+?)\[(\d+)\]$/);
    if (bracketMatch) {
      const arrayName = bracketMatch[1];
      const arrayIndex = parseInt(bracketMatch[2], 10);
      // Add the array container
      segments.push({ key: arrayName, isArrayIndex: false });
      // Add the index
      segments.push({ key: arrayIndex.toString(), isArrayIndex: true, index: arrayIndex });
    } else {
      segments.push({ key: part, isArrayIndex: false });
    }
  });

  return segments;
};

// Helper to reconstruct JSON from database rows
const reconstructJsonFromRows = (rows: DatabaseRow[]): any => {
  const result = {};

  // Sort by path_order to maintain correct structure
  const sortedRows = rows
    .filter(row => row.node_type === 'primitive' || row.node_type === 'value')
    .sort((a, b) => a.path_order.localeCompare(b.path_order));

  sortedRows.forEach(row => {
    setValueByPath(result, row.json_path, row.node_value);
  });

  return compactSparseArrays(result);
};

// Drop the sparse holes (undefined slots) produced by `setValueByPath` when
// the surviving rows of an array name their original indices but a sibling
// at a lower index was deleted. Preserves literal `null` values; only
// `undefined` (sparse) is filtered.
const compactSparseArrays = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.filter((x: any) => x !== undefined).map(compactSparseArrays);
  }
  if (obj && typeof obj === 'object') {
    const out: any = {};
    for (const k of Object.keys(obj)) out[k] = compactSparseArrays(obj[k]);
    return out;
  }
  return obj;
};

// Helper function to set value by path with bracket notation support
const setValueByPath = (obj: any, path: string, value: any): void => {
  if (!path) return;

  const segments = parsePath(path);
  if (segments.length === 0) return;

  let current = obj;

  // Navigate to the parent of the final key
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    const nextSegment = segments[i + 1];

    if (segment.isArrayIndex) {
      // Current segment is an array index
      const idx = segment.index!;
      if (!current[idx]) {
        // Initialize based on what comes next
        current[idx] = nextSegment.isArrayIndex ? [] : {};
      }
      current = current[idx];
    } else {
      // Current segment is a regular key
      if (!current[segment.key]) {
        // Initialize based on what comes next
        current[segment.key] = nextSegment.isArrayIndex ? [] : {};
      }
      current = current[segment.key];
    }
  }

  // Set the final value
  const lastSegment = segments[segments.length - 1];
  if (lastSegment.isArrayIndex) {
    current[lastSegment.index!] = value;
  } else {
    current[lastSegment.key] = value;
  }
};

// Helper function to generate UUID
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper function to generate new row path
const generateNewRowPath = (parentPath: string | null, parentRows: DatabaseRow[]): string => {
  if (!parentPath) {
    // Top-level row - find next available key
    const topLevelKeys = parentRows
      .filter(row => row.depth === 1 && (!row.parent_path || row.parent_path === ''))
      .map(row => {
        const pathParts = row.json_path.split('.');
        return pathParts[pathParts.length - 1];
      })
      .filter(key => key && key.startsWith('newItem'))
      .map(key => {
        const num = parseInt(key.replace('newItem', '')) || 0;
        return num;
      });

    const nextNum = topLevelKeys.length > 0 ? Math.max(...topLevelKeys) + 1 : 1;
    return `newItem${nextNum}`;
  } else {
    // Child row - check if parent is an array
    const parentRow = parentRows.find(r => r.json_path === parentPath);
    const parentIsArray = parentRow?.node_type === 'array';

    if (parentIsArray) {
      // Parent is array - find max array index and use [index] notation
      const childRows = parentRows.filter(row => row.parent_path === parentPath);
      const maxIndex = childRows.reduce((max, row) => {
        const pathParts = row.json_path.split('.');
        const lastPart = pathParts[pathParts.length - 1];
        // Extract number from [N] format
        const match = lastPart.match(/\[(\d+)\]/);
        const index = match ? parseInt(match[1]) : -1;
        return Math.max(max, index);
      }, -1);

      return `${parentPath}[${maxIndex + 1}]`;
    } else {
      // Parent is object - generate newItemN key
      const childRows = parentRows.filter(row => row.parent_path === parentPath);
      const existingKeys = childRows
        .map(row => {
          const pathParts = row.json_path.split('.');
          return pathParts[pathParts.length - 1];
        })
        .filter(key => key && key.startsWith('newItem'))
        .map(key => {
          const num = parseInt(key.replace('newItem', '')) || 0;
          return num;
        });

      const nextNum = existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1;
      return `${parentPath}.newItem${nextNum}`;
    }
  }
};

// Helper function to get next display order
const getNextDisplayOrder = (parentPath: string | null, allRows: DatabaseRow[]): number => {
  const siblingRows = allRows.filter(row => row.parent_path === parentPath);
  if (siblingRows.length === 0) return 0;
  return Math.max(...siblingRows.map(row => row.display_order)) + 1;
};

// Helper function to generate proper path_order for hierarchical sorting
const generatePathOrder = (parentRow: DatabaseRow | null, siblingIndex: number): string => {
  if (!parentRow) {
    // Top-level item
    return String(siblingIndex + 1).padStart(3, '0');
  }

  // Child item - append to parent's path_order
  const parentOrder = parentRow.path_order || '001';
  const childOrder = String(siblingIndex + 1).padStart(3, '0');
  return `${parentOrder}.${childOrder}`;
};

// Helper function to find all descendants of a row (recursively)
const findAllDescendants = (row: DatabaseRow, allRows: DatabaseRow[]): DatabaseRow[] => {
  const descendants: DatabaseRow[] = [];
  const children = allRows.filter(r => r.parent_path === row.json_path);

  for (const child of children) {
    descendants.push(child);
    // Recursively get grandchildren
    descendants.push(...findAllDescendants(child, allRows));
  }

  return descendants;
};

// Helper function to create a dummy/copy value while maintaining JSON validity
const createCopyValue = (originalValue: any): any => {
  // Simply return the original value as-is to avoid JSON encoding issues
  // The user can manually edit the cloned values after creation
  return originalValue;
};

// Helper function to clone a hierarchy with new paths and orders
interface ClonedNode {
  originalRow: DatabaseRow;
  newRow: DatabaseRow;
}

const cloneHierarchy = (
  referenceRow: DatabaseRow,
  descendants: DatabaseRow[],
  newSiblingPathOrder: string,
  newSiblingJsonPath: string,
  newSiblingParentPath: string | null,
  configId: string,
  baseTimestamp: number
): ClonedNode[] => {
  const clonedNodes: ClonedNode[] = [];

  // Map to track old json_path -> new json_path for updating parent references
  const pathMap = new Map<string, string>();

  // First, clone the reference row itself
  const newReferenceRow: DatabaseRow = {
    id: -(baseTimestamp),
    config_id: configId,
    json_path: newSiblingJsonPath,
    path_order: newSiblingPathOrder,
    node_type: referenceRow.node_type,
    node_key: referenceRow.node_key,
    node_value: createCopyValue(referenceRow.node_value),
    parent_path: newSiblingParentPath,
    depth: referenceRow.depth,
    display_order: referenceRow.display_order
  };

  clonedNodes.push({
    originalRow: referenceRow,
    newRow: newReferenceRow
  });

  // Update pathMap
  pathMap.set(referenceRow.json_path, newSiblingJsonPath);

  // Sort descendants by depth to ensure parents are cloned before children
  const sortedDescendants = [...descendants].sort((a, b) => a.depth - b.depth);

  // Clone each descendant
  sortedDescendants.forEach((descendant, index) => {
    // Find the new parent path using the pathMap
    const newParentPath = pathMap.get(descendant.parent_path || '') || newSiblingJsonPath;

    // Generate new json_path based on the descendant's relationship to its parent
    const oldParentPath = descendant.parent_path || '';
    const relativePath = descendant.json_path.substring(oldParentPath.length);
    const newJsonPath = `${newParentPath}${relativePath}`;

    // Store in pathMap for children of this node
    pathMap.set(descendant.json_path, newJsonPath);

    // Calculate new path_order by replacing the reference row's path_order prefix
    let newPathOrder: string;
    if (descendant.path_order.startsWith(referenceRow.path_order)) {
      const suffix = descendant.path_order.substring(referenceRow.path_order.length);
      newPathOrder = `${newSiblingPathOrder}${suffix}`;
    } else {
      // Fallback - shouldn't happen in normal hierarchy
      newPathOrder = descendant.path_order;
    }

    const newDescendantRow: DatabaseRow = {
      id: -(baseTimestamp + index + 1),
      config_id: configId,
      json_path: newJsonPath,
      path_order: newPathOrder,
      node_type: descendant.node_type,
      node_key: descendant.node_key,
      node_value: createCopyValue(descendant.node_value),
      parent_path: newParentPath,
      depth: descendant.depth,
      display_order: descendant.display_order
    };

    clonedNodes.push({
      originalRow: descendant,
      newRow: newDescendantRow
    });
  });

  return clonedNodes;
};

// Helper component to highlight search matches
const HighlightText: React.FC<{ text: string; searchTerm: string; sx?: any }> = ({ text, searchTerm, sx }) => {
  if (!searchTerm.trim()) {
    return <span style={sx}>{text}</span>;
  }

  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return (
    <span style={sx}>
      {parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '0 2px', borderRadius: '2px' }}>
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

// Helper function to search rows for keys and values with qualifiers
const searchRows = (searchTerm: string, allRows: DatabaseRow[]): { matches: DatabaseRow[], pathsToExpand: Set<string> } => {
  if (!searchTerm.trim()) {
    return { matches: [], pathsToExpand: new Set() };
  }

  const originalSearchTerm = searchTerm.trim();
  let actualSearchTerm = originalSearchTerm.toLowerCase();
  let searchMode = 'both'; // default to searching both keys and values
  let searchType = 'contains'; // default to contains

  // Parse search qualifiers
  if (originalSearchTerm.startsWith('key:')) {
    searchMode = 'key';
    actualSearchTerm = originalSearchTerm.substring(4).toLowerCase();
  } else if (originalSearchTerm.startsWith('value:')) {
    searchMode = 'value';
    actualSearchTerm = originalSearchTerm.substring(6).toLowerCase();
  } else if (originalSearchTerm.startsWith('exact:')) {
    searchType = 'exact';
    actualSearchTerm = originalSearchTerm.substring(6).toLowerCase();
  }

  const matches: DatabaseRow[] = [];
  const pathsToExpand = new Set<string>();

  console.log(`🔍 Searching for: "${originalSearchTerm}" (mode: ${searchMode}, type: ${searchType})`);

  allRows.forEach(row => {
    let isMatch = false;
    let matchType = '';
    let matchReason = '';

    // Extract the key name from json_path
    const pathParts = row.json_path.split('.');
    const keyName = pathParts[pathParts.length - 1] || '';
    const cleanKeyName = keyName.replace(/\[\d+\]/g, '');

    // KEY SEARCH (when mode is 'key' or 'both')
    if (searchMode === 'key' || searchMode === 'both') {
      if (searchType === 'exact') {
        // Exact key match only
        if (cleanKeyName.toLowerCase() === actualSearchTerm) {
          isMatch = true;
          matchType = 'exact_key';
          matchReason = `exact key match: "${cleanKeyName}"`;
        }
      } else {
        // Contains match in keys
        if (cleanKeyName.toLowerCase() === actualSearchTerm) {
          isMatch = true;
          matchType = 'exact_key';
          matchReason = `exact key match: "${cleanKeyName}"`;
        } else if (cleanKeyName.toLowerCase().includes(actualSearchTerm)) {
          isMatch = true;
          matchType = 'partial_key';
          matchReason = `partial key match: "${cleanKeyName}" contains "${actualSearchTerm}"`;
        }
      }
    }

    // VALUE SEARCH (when mode is 'value' or 'both', and not already matched)
    if (!isMatch && (searchMode === 'value' || searchMode === 'both') && (row.node_type === 'primitive' || row.node_type === 'value')) {
      const valueStr = String(row.node_value || '').toLowerCase().trim();

      if (searchType === 'exact') {
        // Exact value match only
        if (valueStr === actualSearchTerm) {
          isMatch = true;
          matchType = 'exact_value';
          matchReason = `exact value match: "${valueStr}"`;
        }
      } else {
        // Whole word value match
        if (valueStr === actualSearchTerm) {
          isMatch = true;
          matchType = 'exact_value';
          matchReason = `exact value match: "${valueStr}"`;
        } else if (valueStr.length <= 50) {
          const wordBoundaryRegex = new RegExp(`\\b${actualSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          if (wordBoundaryRegex.test(valueStr)) {
            isMatch = true;
            matchType = 'whole_word_value';
            matchReason = `whole word value match: "${valueStr}" contains "${actualSearchTerm}"`;
          }
        }
      }
    }

    if (isMatch) {
      console.log(`✅ Match found: ${row.json_path} (${matchReason})`);

      matches.push({
        ...row,
        _matchType: matchType
      });

      // Add ALL ancestor paths to ensure the match is visible
      let currentPath = row.parent_path;
      while (currentPath) {
        pathsToExpand.add(currentPath);
        console.log(`📂 Expanding ancestor: ${currentPath}`);

        const parent = allRows.find(r => r.json_path === currentPath);
        currentPath = parent?.parent_path || null;
      }
    }
  });

  console.log(`🎯 Found ${matches.length} total matches`);
  console.log(`📂 Will expand ${pathsToExpand.size} parent paths`);

  if (matches.length === 1) {
    console.log(`⚠️ Only 1 match found. Details:`, matches[0]);
    console.log(`   Parent path: ${matches[0].parent_path}`);
    console.log(`   Paths to expand:`, Array.from(pathsToExpand));
  }

  // Sort matches by priority
  const priorityOrder = {
    'exact_key': 1,
    'partial_key': 2,
    'exact_value': 3,
    'whole_word_value': 4
  };

  matches.sort((a, b) => {
    const aPriority = priorityOrder[a._matchType] || 999;
    const bPriority = priorityOrder[b._matchType] || 999;
    return aPriority - bPriority;
  });

  return { matches, pathsToExpand };
};

// Helper function to detect order mismatches between display_order and path_order
const detectOrderMismatch = (rows: DatabaseRow[]): boolean => {
  // Group rows by parent_path
  const groupedRows = new Map<string | null, DatabaseRow[]>();
  rows.forEach(row => {
    const key = row.parent_path;
    if (!groupedRows.has(key)) {
      groupedRows.set(key, []);
    }
    groupedRows.get(key)!.push(row);
  });

  // Check each group for order mismatches
  for (const [parentPath, siblings] of groupedRows) {
    if (siblings.length <= 1) continue; // No need to check single items

    // Sort by display_order and path_order separately
    const byDisplayOrder = siblings.slice().sort((a, b) => a.display_order - b.display_order);
    const byPathOrder = siblings.slice().sort((a, b) => a.path_order.localeCompare(b.path_order));

    // Compare the orders - if they differ, we have a mismatch
    for (let i = 0; i < siblings.length; i++) {
      if (byDisplayOrder[i].id !== byPathOrder[i].id) {
        console.log(`Order mismatch detected in group ${parentPath}:`,
          {
            byDisplayOrder: byDisplayOrder.map(r => ({ id: r.id, display_order: r.display_order })),
            byPathOrder: byPathOrder.map(r => ({ id: r.id, path_order: r.path_order }))
          });
        return true;
      }
    }
  }

  return false;
};

const DatabaseUIConfigGrid: React.FC<DatabaseUIConfigGridProps> = ({ configId, onChange }) => {
  const apiClient = useApiClient();
  const { currentCustomer } = useCustomer();
  const [rows, setRows] = useState<DatabaseRow[]>([]);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set([''])); // Start with root expanded
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, DatabaseRow>>(new Map());
  const [orderMismatch, setOrderMismatch] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [moveCount, setMoveCount] = useState(0); // Track number of moves made
  const [originalOrder, setOriginalOrder] = useState<{ [key: string]: { display_order: number, path_order: string } }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [searchExpandedPaths, setSearchExpandedPaths] = useState<Set<string>>(new Set());
  const [ociAvailable, setOciAvailable] = useState(false);
  const [ociSyncing, setOciSyncing] = useState(false);
  const [ociFilename, setOciFilename] = useState('AssortmentUiConf.confdefn');
  const [navSyncing, setNavSyncing] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [missingViews, setMissingViews] = useState<string[]>([]);
  const [templateSelections, setTemplateSelections] = useState<{ [viewName: string]: string }>({});
  const [creatingViews, setCreatingViews] = useState(false);
  // Preview + validation dialogs. Preview used to be a permanent panel at
  // the bottom of the page; promoted to a click-to-open dialog so it
  // doesn't eat screen real estate when the operator isn't looking at it.
  const [showPreview, setShowPreview] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  // Perspective filter for the tree. Hides rows whose nearest enclosing
  // perspective-gating container (view / leftNavSection / tab) doesn't
  // match the selection. 'common' means the container has BOTH perspectives.
  const [perspectiveFilter, setPerspectiveFilter] = useState<'all' | 'top-down' | 'bottom-up' | 'common'>('all');
  const apiRef = useGridApiRef();

  // Load data from database
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/ui-config/${encodeURIComponent(configId)}`);
      console.log('Loaded rows from database:', response.data.length);
      console.log('Sample rows:', response.data.slice(0, 10));

      // Store the original order when data is loaded
      const originalOrderMap: { [key: string]: { display_order: number, path_order: string } } = {};
      response.data.forEach((row: DatabaseRow) => {
        originalOrderMap[row.id] = {
          display_order: row.display_order,
          path_order: row.path_order
        };
      });
      setOriginalOrder(originalOrderMap);
      setMoveCount(0); // Reset move count when loading fresh data

      // Debug: Check parent-child relationships
      const parentChildMap = new Map();
      response.data.forEach((row: DatabaseRow) => {
        if (!parentChildMap.has(row.parent_path || 'root')) {
          parentChildMap.set(row.parent_path || 'root', []);
        }
        parentChildMap.get(row.parent_path || 'root').push(row.json_path);
      });
      console.log('Parent-child relationships:', Object.fromEntries(parentChildMap));

      setRows(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load configuration data');
      console.error('Database load error:', err);
    } finally {
      setLoading(false);
    }
  }, [configId, currentCustomer?.tenant, currentCustomer?.environment]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check OCI availability on mount
  useEffect(() => {
    const checkOCI = async () => {
      try {
        const response = await apiClient.get('/ui-config/oci/check');
        setOciAvailable(response.data.available);
        if (response.data.filename) {
          setOciFilename(response.data.filename);
        }
      } catch (error) {
        console.error('Failed to check OCI availability:', error);
        setOciAvailable(false);
      }
    };
    checkOCI();
  }, []);

  // Check for order mismatches whenever rows change or moves are made
  useEffect(() => {
    if (rows.length > 0) {
      // Don't show order mismatch warning when there are pending changes (new unsaved rows)
      // OR when we're in the middle of a save operation (loading = true)
      if (pendingChanges.size > 0 || loading) {
        console.log('🛡️ Suppressing order mismatch check:', { pendingChanges: pendingChanges.size, loading });
        setOrderMismatch(false);
        return;
      }

      // Simple approach: if moves have been made, show mismatch warning
      // This indicates that the current order may differ from the original JSON structure
      if (moveCount > 0) {
        setOrderMismatch(true);
        console.log(`Order changes detected - ${moveCount} move(s) made since last sync`);
      } else {
        // Also check for actual data mismatches (in case of external changes)
        const mismatch = detectOrderMismatch(rows);
        setOrderMismatch(mismatch);
        if (mismatch) {
          console.log('Order mismatch detected - display_order and path_order are out of sync');
        }
      }
    }
  }, [rows, moveCount, pendingChanges, loading]);

  // Handle collapse all functionality
  const handleCollapseAll = useCallback(() => {
    setExpandedPaths(new Set());
  }, []);

  // Handle search execution
  const executeSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm);
  }, [searchTerm]);

  // Handle Enter key press
  const handleSearchKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      executeSearch();
    }
  }, [executeSearch]);

  // Handle search functionality
  useEffect(() => {
    if (rows.length > 0) {
      const { matches, pathsToExpand } = searchRows(activeSearchTerm, rows);
      setSearchResults(matches.map(m => m.id));

      if (activeSearchTerm.trim()) {
        // New search: replace search expansions, keep manual expansions
        // First, identify manual expansions (those not from previous search)
        setExpandedPaths(prev => {
          const manualExpansions = new Set([...prev].filter(p => !searchExpandedPaths.has(p)));
          // Combine manual expansions with new search expansions
          return new Set([...manualExpansions, ...pathsToExpand]);
        });
        // Update search-specific expansions tracker
        setSearchExpandedPaths(pathsToExpand);

        if (matches.length > 0) {
          console.log(`Found ${matches.length} matches for "${activeSearchTerm}"`);
        }
      } else {
        // Search cleared: remove search expansions, keep manual ones
        setExpandedPaths(prev => new Set([...prev].filter(p => !searchExpandedPaths.has(p))));
        setSearchExpandedPaths(new Set());
        console.log('Search cleared - collapsed search results, kept manual expansions');
      }
    }
  }, [activeSearchTerm, rows, searchExpandedPaths]);

  // Filter rows based on expand/collapse state
  // Map every container path (anything that has an `inPerspectives` child)
  // to the SET of perspective values it declares. Used by the filter to
  // decide visibility of a row based on its nearest perspective-gating
  // ancestor. Container path is the row that OWNS the inPerspectives
  // array (e.g. `tabs[0].leftNavSections[1].views[2]`).
  const perspectivesByContainer = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const r of rows) {
      const m = r.json_path.match(/^(.+)\.inPerspectives\[\d+\]$/);
      if (!m) continue;
      if (r.node_type !== 'primitive' && r.node_type !== 'value') continue;
      const v = typeof r.node_value === 'string' ? r.node_value : null;
      if (!v) continue;
      const containerPath = m[1];
      let set = map.get(containerPath);
      if (!set) { set = new Set<string>(); map.set(containerPath, set); }
      set.add(v);
    }
    return map;
  }, [rows]);

  // Given a row's json_path, walk up the tree to find the nearest ancestor
  // (or self) that has an inPerspectives declaration. Returns true if that
  // container matches the active filter, or if no ancestor declares one
  // (rows outside any perspective gate are always visible).
  const matchesPerspectiveFilter = useCallback((rowPath: string): boolean => {
    if (perspectiveFilter === 'all') return true;
    let path = rowPath;
    while (path) {
      const ps = perspectivesByContainer.get(path);
      if (ps && ps.size > 0) {
        if (perspectiveFilter === 'common') {
          return ps.has('top-down') && ps.has('bottom-up');
        }
        return ps.has(perspectiveFilter);
      }
      // Strip the last `.segment` (segment may include bracketed indices
      // like `views[0]`). Bracket-only paths like `tabs[0]` have no dot —
      // when we run out of dots, the lookup above already had its chance
      // on the final ancestor, so we exit.
      const lastDot = path.lastIndexOf('.');
      if (lastDot <= 0) break;
      path = path.substring(0, lastDot);
    }
    return true;
  }, [perspectivesByContainer, perspectiveFilter]);

  const visibleRows = useMemo(() => {
    // Combine existing rows with new rows from pendingChanges
    const existingRows = rows.map(row => {
      const pendingUpdate = pendingChanges.get(row.id);
      return pendingUpdate || row;
    });
    const newRows = Array.from(pendingChanges.values()).filter(row => row.id < 0);
    const allRows = [...existingRows, ...newRows];

    // Sort by path_order to show proper hierarchical order
    const sortedRows = allRows.sort((a, b) => a.path_order.localeCompare(b.path_order));
    const result = [];

    // Helper to check if a row should be visible based on hierarchical expansion
    const isRowVisible = (row: DatabaseRow) => {
      // Skip root entry entirely (depth 0 with empty/null json_path)
      if (row.depth === 0 && (!row.json_path || row.json_path === '')) return false;

      // Root level (depth 0) is always visible (but we've already filtered out the empty root)
      if (row.depth === 0) return true;

      // Level 1 items with empty or null parent_path are always visible (top-level keys)
      if (row.depth === 1 && (!row.parent_path || row.parent_path === '')) return true;

      // For all other items, check if ALL ancestors are expanded
      if (!row.parent_path) return false; // Should have a parent at this depth

      // Check that the immediate parent is expanded
      if (!expandedPaths.has(row.parent_path)) {
        return false;
      }

      // For search results, we should ensure the row is visible if it's a match
      if (searchResults.includes(row.id)) {
        // Double-check that all ancestors are expanded
        let currentPath = row.parent_path;
        while (currentPath) {
          if (!expandedPaths.has(currentPath)) {
            console.warn(`🚨 Search result ${row.json_path} has unexpanded ancestor: ${currentPath}`);
            return false;
          }
          const parent = rows.find(r => r.json_path === currentPath);
          currentPath = parent?.parent_path || null;
        }
        return true;
      }

      return expandedPaths.has(row.parent_path);
    };

    for (const row of sortedRows) {
      if (!isRowVisible(row)) continue;
      if (!matchesPerspectiveFilter(row.json_path)) continue;
      result.push({
        ...row,
        isExpanded: expandedPaths.has(row.json_path),
      });
    }

    // Add debug info about search results vs visible rows
    if (searchResults.length > 0) {
      const visibleSearchResults = result.filter(row => searchResults.includes(row.id));
      console.log(`🔍 Search Results: ${searchResults.length} total, ${visibleSearchResults.length} visible, ${searchResults.length - visibleSearchResults.length} hidden`);

      if (visibleSearchResults.length < searchResults.length) {
        console.log(`❌ Hidden search results:`, searchResults.filter(id => !result.find(row => row.id === id)));
      }
    }

    return result;
  }, [rows, pendingChanges, expandedPaths, searchResults, matchesPerspectiveFilter]);

  // Handle expand/collapse
  const handleToggleExpand = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        // Collapse: remove from expanded set
        newSet.delete(path);
        // Also collapse all descendant paths
        rows.forEach(row => {
          if (row.json_path.startsWith(path + '.') ||
            row.json_path.startsWith(path + '[')) {
            newSet.delete(row.json_path);
          }
        });
      } else {
        // Expand: add to expanded set
        newSet.add(path);
      }
      return newSet;
    });
  }, [rows]);

  // Handle cell value changes
  const handleCellValueChange = useCallback(async (newRow: any, oldRow: any) => {
    // Check if node_value actually changed
    if (newRow.node_value === oldRow.node_value) {
      return oldRow;
    }

    const updatedRow = { ...newRow };

    // Convert value based on node type
    switch (updatedRow.node_type) {
      case 'primitive':
        if (typeof oldRow.node_value === 'number') {
          updatedRow.node_value = parseFloat(newRow.node_value) || 0;
        } else if (typeof oldRow.node_value === 'boolean') {
          updatedRow.node_value = newRow.node_value === 'true' || newRow.node_value === true;
        } else {
          updatedRow.node_value = String(newRow.node_value);
        }
        break;
      default:
        updatedRow.node_value = newRow.node_value;
    }

    // Check if this is an existing row or a new row (negative ID)
    const rowIndex = rows.findIndex(row => row.id === newRow.id);
    const isNewRow = rowIndex === -1;

    if (!isNewRow) {
      // Update existing row in rows array
      const newRows = [...rows];
      newRows[rowIndex] = updatedRow;
      setRows(newRows);
    }

    // Add/update in pending changes (works for both new and existing rows)
    setPendingChanges(prev => {
      const updated = new Map(prev.set(newRow.id, updatedRow));
      // Notify parent of changes with updated pendingChanges
      onChange?.(Array.from(updated.values()));
      return updated;
    });

    return updatedRow;
  }, [rows, pendingChanges, onChange]);

  // Handle key changes for new rows
  const handleKeyChange = useCallback((row: DatabaseRow, newKey: string) => {
    if (row.id > 0) {
      // Don't allow key changes for existing rows (too complex to handle path updates)
      return;
    }

    // For new rows, update the json_path and node_key
    const pathParts = row.json_path.split('.');
    pathParts[pathParts.length - 1] = newKey;
    const newPath = pathParts.join('.');

    const updatedRow = {
      ...row,
      json_path: newPath,
      node_key: newKey
    };

    // Update in pending changes
    setPendingChanges(prev => {
      const updated = new Map(prev.set(row.id, updatedRow));
      onChange?.(Array.from(updated.values()));
      return updated;
    });
  }, [pendingChanges, onChange]);

  // Change a new row's node_type. Only allowed for new (id < 0) rows since
  // changing the type of an existing row would require rewriting its JSON
  // shape and any children. When converting primitive → object/array we
  // reset node_value to {} / []; the reverse converts container → null
  // primitive (children, if any, are untouched — the user can delete them).
  const handleNodeTypeChange = useCallback(
    (row: DatabaseRow, newType: DatabaseRow['node_type']) => {
      if (row.id > 0) return;
      if (row.node_type === newType) return;
      const isContainer = newType === 'object' || newType === 'array';
      const updatedRow: DatabaseRow = {
        ...row,
        node_type: newType,
        node_value: isContainer ? (newType === 'array' ? [] : {}) : null,
      };
      setPendingChanges(prev => {
        const updated = new Map(prev.set(row.id, updatedRow));
        onChange?.(Array.from(updated.values()));
        return updated;
      });
    },
    [onChange]
  );

  // Save changes to database
  const saveChanges = useCallback(async () => {
    if (pendingChanges.size === 0) return;

    console.log('🚀 SAVE CHANGES STARTED');
    console.log('📊 Pending changes:', Array.from(pendingChanges.values()));
    console.log('📊 Pending changes count:', pendingChanges.size);

    try {
      setLoading(true);
      setError(null);

      const changes = Array.from(pendingChanges.values());

      // Separate new rows (negative IDs) from updated rows (positive IDs)
      const newRows = changes.filter(row => row.id < 0);
      const updatedRows = changes.filter(row => row.id > 0);

      console.log('📝 New rows to create:', newRows);
      console.log('✏️ Existing rows to update:', updatedRows);

      // Collect all sibling updates from new rows with 'a' suffix
      const allSiblingUpdates = new Map<number, DatabaseRow>();

      // Sort new rows to process 'a' suffix rows in correct order
      const sortedNewRows = newRows.sort((a, b) => a.path_order.localeCompare(b.path_order));

      // Track processed 'a' suffix patterns to assign sequential path_order
      const processedASuffixes = new Map<string, number>(); // base path -> count of processed

      // First pass: count how many siblings will be inserted at each position
      const insertionCounts = new Map<string, number>(); // target path_order -> count
      sortedNewRows.forEach(row => {
        if (row.path_order.endsWith('a')) {
          const baseOrder = row.path_order.slice(0, -1);
          const parts = baseOrder.split('.');
          const lastPart = parts[parts.length - 1];
          const lastNum = parseInt(lastPart);
          parts[parts.length - 1] = String(lastNum + 1).padStart(3, '0');
          const insertionPoint = parts.join('.');
          insertionCounts.set(insertionPoint, (insertionCounts.get(insertionPoint) || 0) + 1);
        }
      });

      // Handle new rows creation
      // Track mapping of temporary IDs to real database IDs for hierarchical saves
      const tempIdToRealId = new Map<number, number>();
      const tempIdToCreatedRow = new Map<number, DatabaseRow>();

      for (const newRow of sortedNewRows) {
        let response;

        // Keep path_order as-is (including 'a' suffix if present)
        // The 'a' suffix sorts correctly lexicographically: 002 < 002a < 003
        // This avoids shifting other rows' path_orders
        const cleanPathOrder = newRow.path_order;

        if (newRow.parent_path) {
          // Child row - find parent for API call
          // Include both existing rows AND newly created rows (from tempIdToCreatedRow)
          const allCurrentRows = [...rows, ...Array.from(tempIdToCreatedRow.values())];
          const parentRow = allCurrentRows.find(r => r.json_path === newRow.parent_path);
          if (parentRow) {
            response = await apiClient.post(`/ui-config/${encodeURIComponent(configId)}/row/${parentRow.id}/child`, {
              config_id: newRow.config_id,
              json_path: newRow.json_path,
              path_order: cleanPathOrder,
              node_type: newRow.node_type,
              node_key: newRow.node_key,
              node_value: newRow.node_value,
              parent_path: newRow.parent_path,
              depth: newRow.depth,
              display_order: newRow.display_order
            });
          }
        } else {
          // Top-level row - use the regular row endpoint
          response = await apiClient.post(`/ui-config/${encodeURIComponent(configId)}/row`, {
            config_id: newRow.config_id,
            json_path: newRow.json_path,
            path_order: cleanPathOrder,
            node_type: newRow.node_type,
            node_key: newRow.node_key,
            node_value: newRow.node_value,
            parent_path: newRow.parent_path,
            depth: newRow.depth,
            display_order: newRow.display_order
          });
        }

        if (response) {
          // Add the new row to rows with the real ID from database
          const createdRow = response.data;
          setRows(prev => [...prev, createdRow]);

          // Track the mapping for child rows to reference
          tempIdToRealId.set(newRow.id, createdRow.id);
          tempIdToCreatedRow.set(newRow.id, createdRow);
        }
      }

      // Add all collected sibling updates to pending changes
      if (allSiblingUpdates.size > 0) {
        allSiblingUpdates.forEach((updatedRow, id) => {
          pendingChanges.set(id, updatedRow);
        });
      }

      // Re-separate updated rows after adding sibling updates
      const finalUpdatedRows = Array.from(pendingChanges.values()).filter(row => row.id > 0);

      // Handle updated rows
      if (finalUpdatedRows.length > 0) {
        console.log('📤 Sending bulk update request:', finalUpdatedRows);
        console.log('📤 Detailed bulk update data:', finalUpdatedRows.map(row => ({
          id: row.id,
          node_value: row.node_value,
          display_order: row.display_order,
          node_type: row.node_type
        })));
        try {
          const response = await apiClient.put(`/ui-config/${encodeURIComponent(configId)}/bulk`, finalUpdatedRows);
          console.log('✅ Bulk update response:', response.data);
        } catch (bulkError) {
          console.error('❌ Bulk update failed:', bulkError.response?.data || bulkError.message);
          throw bulkError;
        }
      }

      setPendingChanges(new Map());

      // Reload data to get clean state and prevent order mismatch warnings
      await loadData();

      // Reset order mismatch state after successful save
      setOrderMismatch(false);
      setMoveCount(0);

      // Add a small delay to prevent order mismatch detection from immediately running after reload
      setTimeout(() => {
        setOrderMismatch(false);
        setMoveCount(0);
      }, 100);

      console.log('✅ SAVE COMPLETED SUCCESSFULLY');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save changes');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  }, [configId, pendingChanges, rows]);


  // Add child row
  const handleAddChild = useCallback((parentRow: DatabaseRow) => {
    try {
      setError(null);
      // Include both existing and new rows when generating paths and orders
      const allCurrentRows = [...rows, ...Array.from(pendingChanges.values()).filter(r => r.id < 0)];
      const newPath = generateNewRowPath(parentRow.json_path, allCurrentRows);
      const newDisplayOrder = getNextDisplayOrder(parentRow.json_path, allCurrentRows);

      // Generate proper path_order for hierarchical sorting. Bug fix:
      // count-based was wrong when a prior sibling was deleted — count
      // collapses to N but existing siblings may already occupy suffixes
      // > N, causing the new row to collide. Use MAX(existing suffix) + 1.
      const siblings = allCurrentRows.filter(row => row.parent_path === parentRow.json_path);
      const maxSuffix = siblings.reduce((max, s) => {
        const parts = (s.path_order || '').split('.');
        const n = parseInt(parts[parts.length - 1], 10) || 0;
        return Math.max(max, n);
      }, 0);
      const newPathOrder = generatePathOrder(parentRow, maxSuffix);

      // Generate a temporary negative ID for the new row (will get real ID when saved)
      const tempId = -(Date.now());

      const newRow: DatabaseRow = {
        id: tempId,
        config_id: configId,
        json_path: newPath,
        path_order: newPathOrder, // Use hierarchical path_order instead of json_path
        node_type: 'primitive',
        node_key: newPath.split('.').pop() || '',
        node_value: null, // Use null instead of empty string for JSON compatibility
        parent_path: parentRow.json_path,
        depth: parentRow.depth + 1,
        display_order: newDisplayOrder
      };

      // Add ONLY to pendingChanges for immediate UI update and Save Changes activation
      // Don't add to rows until saved - this keeps logic simpler
      setPendingChanges(prev => {
        const updated = new Map(prev.set(tempId, newRow));
        // Notify parent of changes with updated pendingChanges
        onChange?.(Array.from(updated.values()));
        return updated;
      });

      // Auto-expand parent to show the new child
      setExpandedPaths(prev => new Set(prev.add(parentRow.json_path)));
    } catch (err: any) {
      setError('Failed to add child row');
      console.error('Add child error:', err);
    }
  }, [configId, rows, pendingChanges, onChange]);

  // Helper function to recursively update children's path_order when parent changes
  const updateChildrenPathOrder = useCallback((allRows: DatabaseRow[], parentRow: DatabaseRow, oldPathOrder: string, newPathOrder: string): Map<number, DatabaseRow> => {
    const updates = new Map<number, DatabaseRow>();

    // Find all direct children of this parent
    const children = allRows.filter(row => row.parent_path === parentRow.json_path);

    children.forEach(child => {
      if (child.id > 0 && child.path_order.startsWith(oldPathOrder + '.')) {
        // Update child's path_order by replacing the parent's old path_order with new one
        const childSuffix = child.path_order.substring(oldPathOrder.length);
        const updatedChildPathOrder = newPathOrder + childSuffix;

        const updatedChild = { ...child, path_order: updatedChildPathOrder };
        updates.set(child.id, updatedChild);

        // Recursively update this child's children
        const grandchildUpdates = updateChildrenPathOrder(allRows, child, child.path_order, updatedChildPathOrder);
        grandchildUpdates.forEach((updatedRow, id) => {
          updates.set(id, updatedRow);
        });
      }
    });

    return updates;
  }, []);

  // Add sibling row (at the same level as the current row) - with hierarchical cloning support
  const handleAddSibling = useCallback((referenceRow: DatabaseRow) => {
    try {
      setError(null);
      // Include both existing and new rows when generating paths and orders
      const allCurrentRows = [...rows, ...Array.from(pendingChanges.values()).filter(r => r.id < 0)];

      // Find all descendants of the reference row
      const descendants = findAllDescendants(referenceRow, allCurrentRows);
      const hasChildren = descendants.length > 0;

      // Find the parent of the reference row (or null for top-level)
      const parentPath = referenceRow.parent_path;

      // For hierarchical cloning, always generate a unique path to avoid conflicts
      // This ensures no duplicate keys and proper JSON structure
      const newPath = generateNewRowPath(parentPath, allCurrentRows);

      const newDisplayOrder = getNextDisplayOrder(parentPath, allCurrentRows);

      // Find all siblings (same parent) and sort by path_order to understand current order
      const siblings = allCurrentRows
        .filter(row => row.parent_path === parentPath)
        .sort((a, b) => a.path_order.localeCompare(b.path_order));

      // Find reference row's position in sorted siblings
      const referenceIndex = siblings.findIndex(row => row.id === referenceRow.id);

      let newPathOrder: string;

      if (referenceIndex === -1 || referenceIndex === siblings.length - 1) {
        // Reference is the last sibling (or not found) — append at the end.
        // Pick MAX(existing siblings' numeric suffix) + 1 rather than
        // referenceRow's own suffix + 1. Equivalent when ref IS the
        // highest-suffix sibling; safer when (a) referenceIndex === -1
        // (ref not in siblings list) or (b) there's some other anomaly
        // where ref's suffix is not actually the max. Mirrors the
        // hardening applied to handleAddChild.
        const refOrder = referenceRow.path_order;
        const parts = refOrder.split('.');
        const maxSuffix = siblings.reduce((max, s) => {
          const sParts = (s.path_order || '').split('.');
          const n = parseInt(sParts[sParts.length - 1], 10) || 0;
          return Math.max(max, n);
        }, parseInt(parts[parts.length - 1], 10) || 0);
        parts[parts.length - 1] = String(maxSuffix + 1).padStart(3, '0');
        newPathOrder = parts.join('.');
      } else {
        // Reference is in middle - insert between reference and next sibling.
        // Use suffix letters (a/b/c) so the new row sorts immediately
        // after reference. Collision check stays — but extend it to ALL
        // rows under the same parent (not just sorted siblings) to harden
        // against any pending-state weirdness.
        const refOrder = referenceRow.path_order;
        const taken = new Set(siblings.map(s => s.path_order));
        let suffix = 'a';
        let candidatePathOrder = refOrder + suffix;
        while (taken.has(candidatePathOrder)) {
          suffix = String.fromCharCode(suffix.charCodeAt(0) + 1);
          candidatePathOrder = refOrder + suffix;
        }
        newPathOrder = candidatePathOrder;
      }

      const baseTimestamp = Date.now();

      if (hasChildren) {
        // Clone the entire hierarchy
        console.log(`Cloning hierarchy: reference row + ${descendants.length} descendants`);

        const clonedNodes = cloneHierarchy(
          referenceRow,
          descendants,
          newPathOrder,
          newPath,
          parentPath,
          configId,
          baseTimestamp
        );

        // Add all cloned nodes to pendingChanges
        setPendingChanges(prev => {
          const updated = new Map(prev);
          clonedNodes.forEach(({ newRow }) => {
            updated.set(newRow.id, newRow);
          });
          // Notify parent of changes
          onChange?.(Array.from(updated.values()));
          return updated;
        });

        // Auto-expand the new sibling to show its children
        setExpandedPaths(prev => {
          const newExpanded = new Set(prev);
          newExpanded.add(newPath); // Expand the new sibling
          if (parentPath) {
            newExpanded.add(parentPath); // Expand parent to show the new sibling
          }
          return newExpanded;
        });

        console.log(`Successfully cloned ${clonedNodes.length} nodes`);
      } else {
        // No children - simple single row addition (original behavior)
        const tempId = -(baseTimestamp);

        const newRow: DatabaseRow = {
          id: tempId,
          config_id: configId,
          json_path: newPath,
          path_order: newPathOrder,
          node_type: 'primitive',
          node_key: newPath.split('.').pop() || '',
          node_value: null,
          parent_path: parentPath,
          depth: referenceRow.depth,
          display_order: newDisplayOrder
        };

        // Add ONLY to pendingChanges for immediate UI update and Save Changes activation
        setPendingChanges(prev => {
          const updated = new Map(prev.set(tempId, newRow));
          // Notify parent of changes with updated pendingChanges
          onChange?.(Array.from(updated.values()));
          return updated;
        });

        // Auto-expand parent if it exists to show the new sibling
        if (parentPath) {
          setExpandedPaths(prev => new Set(prev.add(parentPath)));
        }
      }
    } catch (err: any) {
      setError('Failed to add sibling row');
      console.error('Add sibling error:', err);
    }
  }, [configId, rows, pendingChanges, onChange]);

  // Delete row and its children
  const handleDeleteRow = useCallback(async (rowToDelete: DatabaseRow) => {
    // Find all descendants to delete
    const toDelete = [rowToDelete];
    const findDescendants = (parentPath: string) => {
      const allCurrentRows = [...rows, ...Array.from(pendingChanges.values()).filter(r => r.id < 0)];
      const children = allCurrentRows.filter(row => row.parent_path === parentPath);
      children.forEach(child => {
        toDelete.push(child);
        findDescendants(child.json_path);
      });
    };
    findDescendants(rowToDelete.json_path);

    // Check if deleting existing rows (requires confirmation)
    const existingRows = toDelete.filter(row => row.id > 0);
    const newRows = toDelete.filter(row => row.id < 0);

    if (existingRows.length > 0) {
      const confirmMessage = toDelete.length > 1
        ? `Delete "${rowToDelete.json_path}" and ${toDelete.length - 1} child rows? This will permanently remove them from the database.`
        : `Delete "${rowToDelete.json_path}"? This will permanently remove it from the database.`;

      if (!window.confirm(confirmMessage)) {
        return; // User cancelled
      }
    }

    try {
      setError(null);

      // Delete existing rows from database
      if (existingRows.length > 0) {
        await apiClient.delete(`/ui-config/${encodeURIComponent(configId)}/row/${rowToDelete.id}`);
      }

      // Remove existing rows from UI state, new rows are only in pendingChanges
      const existingRowIds = existingRows.map(row => row.id);
      setRows(prev => prev.filter(row => !existingRowIds.includes(row.id)));

      // Remove all rows (both new and existing) from pending changes
      setPendingChanges(prev => {
        const updated = new Map(prev);
        toDelete.forEach(row => updated.delete(row.id));
        return updated;
      });

      // Adjust path_order and display_order for subsequent siblings after deletion
      const allCurrentRows = [...rows.filter(row => !existingRowIds.includes(row.id)),
      ...Array.from(pendingChanges.values()).filter(r => r.id < 0 && !toDelete.find(d => d.id === r.id))];

      const deletedRow = rowToDelete;
      const parentPath = deletedRow.parent_path;

      // Find all siblings that come after the deleted row and need their orders adjusted
      const siblingsByPathOrder = allCurrentRows
        .filter(row => row.parent_path === parentPath)
        .sort((a, b) => a.path_order.localeCompare(b.path_order));

      const siblingsByDisplayOrder = allCurrentRows
        .filter(row => row.parent_path === parentPath)
        .sort((a, b) => a.display_order - b.display_order);

      // Update display_order for siblings that come after the deleted row
      // NOTE: We do NOT adjust path_order - it should remain stable to preserve JSON structure
      const adjustmentUpdates = new Map<number, DatabaseRow>();

      // Only adjust display_order for siblings that had higher display_order than deleted row
      siblingsByDisplayOrder.forEach(sibling => {
        if (sibling.id > 0 && sibling.display_order > deletedRow.display_order) {
          const existingUpdate = adjustmentUpdates.get(sibling.id) || { ...sibling };
          adjustmentUpdates.set(sibling.id, { ...existingUpdate, display_order: sibling.display_order - 1 });
        }
      });

      // Send adjustment updates immediately to database
      if (adjustmentUpdates.size > 0) {
        const adjustmentArray = Array.from(adjustmentUpdates.values());
        await apiClient.put(`/ui-config/${encodeURIComponent(configId)}/bulk`, adjustmentArray);

        // Also update local state
        setRows(prev => prev.map(row => {
          const update = adjustmentUpdates.get(row.id);
          return update ? { ...row, ...update } : row;
        }));
      }

      onChange?.(toDelete);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete row');
      console.error('Delete row error:', err);
    }
  }, [configId, rows, pendingChanges, onChange]);


  // Reorder helper: swap a row with its adjacent sibling
  // (`direction = -1` for up, `+1` for down).
  //
  // Strategy:
  //   1. Swap path_order + display_order between the two rows. Rewrite both
  //      subtrees' path_order prefixes so descendants follow.
  //   2. If the siblings are array elements (json_path ends in [N]),
  //      RENUMBER every sibling's json_path bracket index to match its new
  //      sorted position. This both performs the current swap and heals any
  //      pre-existing index/path_order drift (e.g. from earlier moves done
  //      under a buggy version). Descendants of any renumbered sibling get
  //      their json_path/parent_path prefix rewritten.
  //
  // For object properties: step 1 is enough — the Preview's
  // reconstructJsonFromRows iterates sorted by path_order, so property
  // insertion order follows.
  //
  // Existing rows (id > 0) commit via the bulk endpoint; pending rows
  // (id < 0) update locally in pendingChanges.
  const handleMoveRow = useCallback(async (row: DatabaseRow, direction: -1 | 1) => {
    try {
      setError(null);
      const allCurrentRows = [...rows, ...Array.from(pendingChanges.values()).filter(r => r.id < 0)];

      const siblings = allCurrentRows
        .filter(r => r.parent_path === row.parent_path)
        .sort((a, b) => a.path_order.localeCompare(b.path_order));

      const currentIndex = siblings.findIndex(s => s.id === row.id);
      const partnerIndex = currentIndex + direction;
      if (currentIndex < 0 || partnerIndex < 0 || partnerIndex >= siblings.length) {
        return;
      }
      const partner = siblings[partnerIndex];

      const rowOldPathOrder = row.path_order;
      const partnerOldPathOrder = partner.path_order;
      const rowOldDisplayOrder = row.display_order;
      const partnerOldDisplayOrder = partner.display_order;

      const rewritePrefix = (oldPath: string, oldPrefix: string, newPrefix: string): string => {
        if (oldPath.startsWith(oldPrefix)) {
          return newPrefix + oldPath.substring(oldPrefix.length);
        }
        return oldPath;
      };

      // Accumulate patches keyed by row id (merge later if multiple steps
      // touch the same row).
      const patches = new Map<number, Partial<DatabaseRow>>();
      const accumulate = (id: number, patch: Partial<DatabaseRow>) => {
        const existing = patches.get(id) || {};
        patches.set(id, { ...existing, ...patch });
      };

      // STEP 1: swap path_order + display_order, rewrite descendants' path_order prefix.
      accumulate(row.id, { path_order: partnerOldPathOrder, display_order: partnerOldDisplayOrder });
      accumulate(partner.id, { path_order: rowOldPathOrder, display_order: rowOldDisplayOrder });
      findAllDescendants(row, allCurrentRows).forEach(d => {
        accumulate(d.id, { path_order: rewritePrefix(d.path_order, rowOldPathOrder, partnerOldPathOrder) });
      });
      findAllDescendants(partner, allCurrentRows).forEach(d => {
        accumulate(d.id, { path_order: rewritePrefix(d.path_order, partnerOldPathOrder, rowOldPathOrder) });
      });

      // STEP 2: array sibling renumber (only if every sibling looks like an array element).
      const arrayIdxRe = /\[(\d+)\]$/;
      const isArrayParent = siblings.length > 0 && siblings.every(s => arrayIdxRe.test(s.json_path));

      if (isArrayParent) {
        // Effective path_order per sibling = patched value if any, else original.
        const effPathOrder = (s: DatabaseRow) =>
          (patches.get(s.id)?.path_order ?? s.path_order);
        const newSortedSiblings = [...siblings].sort((a, b) =>
          effPathOrder(a).localeCompare(effPathOrder(b))
        );

        // Lookup: id -> rowobj (we'll mutate json_path through patches).
        const rowsById = new Map(allCurrentRows.map(r => [r.id, r]));

        // Two-phase renumber: first compute every sibling's target json_path,
        // then commit (using a temp marker for descendants would also work,
        // but since we operate on patches, not the rows themselves, no
        // intermediate-collision risk exists).
        const newJsonPathBySiblingId = new Map<number, string>();
        newSortedSiblings.forEach((s, newIdx) => {
          const desiredPath = s.json_path.replace(arrayIdxRe, `[${newIdx}]`);
          newJsonPathBySiblingId.set(s.id, desiredPath);
        });

        newSortedSiblings.forEach(s => {
          const oldJsonPath = s.json_path;
          const newJsonPath = newJsonPathBySiblingId.get(s.id)!;
          if (oldJsonPath === newJsonPath) return; // no change

          accumulate(s.id, { json_path: newJsonPath });

          // Rewrite this sibling's descendants' json_path and parent_path
          // prefixes. Use the row's CURRENT json_path (not any patched
          // intermediate) as the old prefix.
          findAllDescendants(s, allCurrentRows).forEach(d => {
            accumulate(d.id, {
              json_path: rewritePrefix(d.json_path, oldJsonPath, newJsonPath),
              parent_path: rewritePrefix(d.parent_path, oldJsonPath, newJsonPath)
            });
          });
        });
      }

      // Build the backend payload and the local-state updates.
      const updates: Array<Partial<DatabaseRow> & { id: number }> = [];
      patches.forEach((patch, id) => {
        if (id > 0) {
          updates.push({ id, ...patch });
        }
      });

      if (updates.length > 0) {
        await apiClient.put(`/ui-config/${encodeURIComponent(configId)}/bulk`, updates);
      }

      setRows(prev => prev.map(r => {
        const patch = patches.get(r.id);
        return patch ? { ...r, ...patch } : r;
      }));
      setPendingChanges(prev => {
        const next = new Map(prev);
        patches.forEach((patch, id) => {
          const existing = next.get(id);
          if (existing) next.set(id, { ...existing, ...patch });
        });
        return next;
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reorder row');
      console.error('Move row error:', err);
    }
  }, [configId, rows, pendingChanges, apiClient]);

  const handleMoveUp = useCallback((row: DatabaseRow) => handleMoveRow(row, -1), [handleMoveRow]);
  const handleMoveDown = useCallback((row: DatabaseRow) => handleMoveRow(row, 1), [handleMoveRow]);


  // Sync path_order to match display_order (make JSON order match visual order)
  const handleSyncOrder = useCallback(async () => {
    try {
      setSyncInProgress(true);
      setError(null);

      // This should update path_order to match the current visual order (display_order)
      // So the JSON structure will reflect the moves the user made

      // Group rows by parent_path and sort by display_order
      const groupedRows = new Map<string | null, DatabaseRow[]>();
      rows.forEach(row => {
        const key = row.parent_path;
        if (!groupedRows.has(key)) {
          groupedRows.set(key, []);
        }
        groupedRows.get(key)!.push(row);
      });

      const updates: any[] = [];

      // For each group, assign new path_order based on display_order sequence
      for (const [parentPath, siblings] of groupedRows) {
        const sortedByDisplay = siblings.slice().sort((a, b) => a.display_order - b.display_order);

        sortedByDisplay.forEach((row, index) => {
          // Generate new path_order based on the visual position
          const paddedIndex = String(index + 1).padStart(3, '0');
          let newPathOrder: string;

          if (!parentPath) {
            // Top-level item
            newPathOrder = paddedIndex;
          } else {
            // Child item - inherit parent's path_order and append our index
            const parentRow = rows.find(r => r.json_path === parentPath);
            if (parentRow) {
              newPathOrder = `${parentRow.path_order}.${paddedIndex}`;
            } else {
              newPathOrder = `${parentPath}.${paddedIndex}`;
            }
          }

          if (newPathOrder !== row.path_order) {
            updates.push({
              id: row.id,
              path_order: newPathOrder,
              display_order: row.display_order
            });
          }
        });
      }

      if (updates.length > 0) {
        await apiClient.put(`/ui-config/${encodeURIComponent(configId)}/sync-order`, updates);

        // Update local state
        setRows(prev => prev.map(row => {
          const update = updates.find(u => u.id === row.id);
          return update ? { ...row, path_order: update.path_order } : row;
        }));

        console.log(`Synced ${updates.length} rows - JSON order now matches visual order`);
      }

      setOrderMismatch(false);
      setMoveCount(0); // Reset move counter after sync
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sync order');
      console.error('Sync order error:', err);
    } finally {
      setSyncInProgress(false);
    }
  }, [configId, rows]);

  // Revert to JSON: set display_order = path_order and re-render
  const handleRevertOrder = useCallback(async () => {
    try {
      setSyncInProgress(true);
      setError(null);

      // Simply copy path_order sequence to display_order
      const updates: any[] = [];

      // Sort rows by path_order to get correct JSON sequence
      const sortedByPath = rows.slice().sort((a, b) => a.path_order.localeCompare(b.path_order));

      sortedByPath.forEach((row, index) => {
        if (row.display_order !== index) {
          updates.push({
            id: row.id,
            display_order: index,
            path_order: row.path_order // Never change path_order
          });
        }
      });

      if (updates.length > 0) {
        await apiClient.put(`/ui-config/${encodeURIComponent(configId)}/reorder`, updates);

        // Update local state immediately
        setRows(prev => prev.map(row => {
          const update = updates.find(u => u.id === row.id);
          return update ? { ...row, display_order: update.display_order } : row;
        }));

        console.log(`Reverted ${updates.length} rows - display_order now matches path_order`);
      }

      setOrderMismatch(false);
      setMoveCount(0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revert order');
      console.error('Revert order error:', err);
    } finally {
      setSyncInProgress(false);
    }
  }, [configId, rows]);

  // Generate current JSON from database rows (including pending changes)
  const currentJson = useMemo(() => {
    // Start with all existing rows and apply pending updates
    const updatedExistingRows = rows.map(row => {
      const pendingUpdate = pendingChanges.get(row.id);
      return pendingUpdate || row;
    });

    // Add new rows from pendingChanges (rows with negative IDs)
    const newRows = Array.from(pendingChanges.values()).filter(row => row.id < 0);


    // Combine all rows for JSON reconstruction
    const allRows = [...updatedExistingRows, ...newRows];

    return reconstructJsonFromRows(allRows);
  }, [rows, pendingChanges]);

  // Validator. Three checks (per the spec):
  //   1. The reconstructed structure must round-trip through JSON.
  //   2. Every `inPerspectives` array element must be 'top-down' or 'bottom-up'.
  //   3. Across views grouped by `id`, no (id, perspective) pair may repeat.
  //      A length-2 inPerspectives array must be exactly {top-down, bottom-up}.
  const validateConfig = useCallback((): string[] => {
    const issues: string[] = [];

    // (1) Structure: try to serialize. If it throws (cycles, etc.), flag.
    let serialized: string;
    try {
      serialized = JSON.stringify(currentJson);
      JSON.parse(serialized);
    } catch (e: any) {
      issues.push(`Config does not serialize to valid JSON: ${e?.message || e}`);
      return issues;  // can't run downstream checks if the structure is broken
    }

    const ALLOWED = new Set(['top-down', 'bottom-up']);

    // (2) Walk the entire tree once, collect every inPerspectives array
    //     plus its surrounding view's id (if any).
    // (3) While walking, group view perspectives by id.
    // Per the user's rule: the (id, perspective) uniqueness check is scoped
    // to a single leftNavSection. Across different sections the same id +
    // perspective is allowed (e.g. STYLE REVIEW and STYLE COLOR REVIEW can
    // each have a 'CollectionView/bottom-up'). Key the map by section.
    const idToPerspectivesBySection = new Map<
      string,
      Map<string, Map<string, string>>
    >();

    const friendlyLabel = (item: any, idx: number): string => {
      if (item && typeof item === 'object') {
        if (typeof item.name === 'string' && item.name) return item.name;
        if (typeof item.id === 'string' && item.id) return item.id;
      }
      return `#${idx}`;
    };

    const walk = (
      node: any,
      path: string,
      friendly: string,
      enclosingViewId: string | null,
      parentKey: string | null,
      sectionScope: string  // '' at top level; set to leftNavSection identity inside one
    ) => {
      if (Array.isArray(node)) {
        node.forEach((item, i) => {
          const childPath = `${path}[${i}]`;
          const NAV_ARRAYS = new Set(['tabs', 'leftNavSections', 'views']);
          let childFriendly: string;
          if (parentKey && NAV_ARRAYS.has(parentKey)) {
            const label = friendlyLabel(item, i);
            childFriendly = friendly ? `${friendly} › ${label}` : label;
          } else {
            childFriendly = `${friendly}[${i}]`;
          }
          // Entering a leftNavSections[i] establishes the section scope used
          // for the duplicate-(id,perspective) check below. We use the
          // section's id when available, falling back to its index — the
          // value doesn't matter as long as it's unique per section.
          let childScope = sectionScope;
          if (parentKey === 'leftNavSections') {
            const id = (item && typeof item === 'object' && typeof item.id === 'string') ? item.id : `idx:${i}`;
            childScope = id;
          }
          walk(item, childPath, childFriendly, enclosingViewId, null, childScope);
        });
        return;
      }
      if (node && typeof node === 'object') {
        const isViewLike = typeof node.id === 'string' && Array.isArray(node.inPerspectives);
        const myId: string | null = isViewLike ? node.id : enclosingViewId;

        if (Array.isArray(node.inPerspectives)) {
          for (let i = 0; i < node.inPerspectives.length; i++) {
            const v = node.inPerspectives[i];
            if (typeof v !== 'string' || !ALLOWED.has(v)) {
              issues.push(
                `Invalid inPerspectives value at ${friendly} (${path}.inPerspectives[${i}]): ` +
                `${JSON.stringify(v)} (expected 'top-down' or 'bottom-up')`
              );
            }
          }
          if (node.inPerspectives.length === 2) {
            const set = new Set(node.inPerspectives.filter((x: any) => typeof x === 'string'));
            if (!(set.has('top-down') && set.has('bottom-up'))) {
              issues.push(
                `${friendly} (${path}).inPerspectives has two values but they aren't ` +
                `{'top-down', 'bottom-up'}: ${JSON.stringify(node.inPerspectives)}`
              );
            }
          }
          if (node.inPerspectives.length > 2) {
            issues.push(
              `${friendly} (${path}).inPerspectives has more than 2 values: ` +
              `${JSON.stringify(node.inPerspectives)}`
            );
          }

          if (typeof node.id === 'string') {
            // Scope the (id, perspective) uniqueness to the current section.
            // If we're not inside a leftNavSection (e.g. tab-level config),
            // use the empty scope key — that bucket is its own scope.
            let sectionMap = idToPerspectivesBySection.get(sectionScope);
            if (!sectionMap) {
              sectionMap = new Map<string, Map<string, string>>();
              idToPerspectivesBySection.set(sectionScope, sectionMap);
            }
            let perspectivesForId = sectionMap.get(node.id);
            if (!perspectivesForId) {
              perspectivesForId = new Map<string, string>();
              sectionMap.set(node.id, perspectivesForId);
            }
            for (const v of node.inPerspectives) {
              if (typeof v === 'string' && ALLOWED.has(v)) {
                if (perspectivesForId.has(v)) {
                  issues.push(
                    `Two views in the same leftNavSection share id='${node.id}' AND inPerspectives='${v}'. ` +
                    `First seen at ${perspectivesForId.get(v)}, repeated at ${friendly}. ` +
                    `(Within one section, each (id, perspective) pair must be unique.)`
                  );
                } else {
                  perspectivesForId.set(v, friendly);
                }
              }
            }
          }
        }

        for (const k of Object.keys(node)) {
          walk(node[k], path ? `${path}.${k}` : k, friendly, myId, k, sectionScope);
        }
      }
    };
    walk(currentJson, '', '', null, null, '');

    return issues;
  }, [currentJson]);

  const runValidation = useCallback(() => {
    setValidationIssues(validateConfig());
    setShowValidation(true);
  }, [validateConfig]);

  // Export current state as JSON
  const exportAsJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(currentJson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = ociFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentJson, ociFilename]);

  // Sync to OCI
  const handleSyncToOCI = useCallback(async () => {
    // Confirm action
    if (!window.confirm('This will save all pending changes and sync the configuration to OCI. Continue?')) {
      return;
    }

    try {
      setOciSyncing(true);
      setError(null);

      // Save pending changes first if any
      if (pendingChanges.size > 0) {
        await saveChanges();
        // Wait a moment for save to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Sync to OCI
      const response = await apiClient.post(`/ui-config/${encodeURIComponent(configId)}/sync-to-oci`, currentJson);

      if (response.data.success) {
        alert('Configuration synced to OCI successfully!');
      } else {
        throw new Error(response.data.error || 'Sync failed');
      }

    } catch (err: any) {
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to sync to OCI';
      setError(errorMessage);
      alert(`Sync failed: ${errorMessage}`);
      console.error('OCI sync error:', err);
    } finally {
      setOciSyncing(false);
    }
  }, [configId, currentJson, pendingChanges, saveChanges]);

  // Sync Navigation to View Manager
  const handleSyncNavigation = useCallback(async () => {
    try {
      setNavSyncing(true);
      setError(null);

      const response = await apiClient.post(`/conf/${encodeURIComponent(configId)}/sync-navigation`);

      if (response.data.ok) {
        // Check if there are missing views
        if (response.data.missingViews && response.data.missingViews.length > 0) {
          console.log('Missing views detected:', response.data.missingViews);
          setMissingViews(response.data.missingViews);

          // Initialize template selections with 'grid' as default for all views
          const defaultSelections: { [key: string]: string } = {};
          response.data.missingViews.forEach((viewName: string) => {
            defaultSelections[viewName] = 'grid';
          });
          setTemplateSelections(defaultSelections);

          // Show the modal
          setShowTemplateModal(true);
        } else {
          alert('Navigation synced to View Manager successfully!');
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to sync navigation';
      setError(errorMessage);
      alert(`Navigation sync failed: ${errorMessage}`);
      console.error('Navigation sync error:', err);
    } finally {
      setNavSyncing(false);
    }
  }, [configId]);

  // Handle template selection change
  const handleTemplateChange = useCallback((viewName: string, templateType: string) => {
    setTemplateSelections(prev => ({
      ...prev,
      [viewName]: templateType
    }));
  }, []);

  // Handle creating views with selected templates
  const handleCreateViews = useCallback(async () => {
    try {
      setCreatingViews(true);
      setError(null);

      // Prepare the request payload
      const views = missingViews.map(viewName => ({
        viewName,
        templateType: templateSelections[viewName] || 'grid'
      }));

      console.log('Creating views with selections:', views);

      const response = await apiClient.post('/view-templates/create-batch', { views });

      if (response.data.ok) {
        const successCount = response.data.results.filter((r: any) => r.success).length;
        const failCount = response.data.results.filter((r: any) => !r.success).length;

        let message = `Successfully created ${successCount} view(s).`;
        if (failCount > 0) {
          message += ` ${failCount} view(s) failed.`;
          const failedViews = response.data.results
            .filter((r: any) => !r.success)
            .map((r: any) => `${r.viewName}: ${r.error}`)
            .join('\n');
          console.error('Failed views:', failedViews);
        }

        alert(message);

        // Close the modal
        setShowTemplateModal(false);
        setMissingViews([]);
        setTemplateSelections({});
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create views';
      setError(errorMessage);
      alert(`View creation failed: ${errorMessage}`);
      console.error('View creation error:', err);
    } finally {
      setCreatingViews(false);
    }
  }, [missingViews, templateSelections]);

  // Handle closing the template modal
  const handleCloseTemplateModal = useCallback(() => {
    setShowTemplateModal(false);
    setMissingViews([]);
    setTemplateSelections({});
  }, []);

  // Stable column definitions - NOTE: Don't depend on expandedPaths to prevent column reset
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'json_path',
      headerName: 'Path',
      width: 600,
      resizable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        const row = params.row as DatabaseRow;
        const canExpand = row.node_type === 'object' || row.node_type === 'array';

        // Check if this container actually has children in the database
        const hasChildren = canExpand && rows.some(r => r.parent_path === row.json_path);

        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: Math.max(0, row.depth - 1) * 15,
            minHeight: 36,
            maxWidth: '100%'
          }}>
            <Box sx={{ width: 20, display: 'flex', justifyContent: 'center' }}>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(row.json_path);
                  }}
                  sx={{ p: 0.25 }}
                >
                  {expandedPaths.has(row.json_path) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: canExpand ? 'primary.main' : 'text.primary',
                fontWeight: canExpand ? 600 : 400,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={row.json_path}
            >
              <HighlightText
                text={row.json_path || 'root'}
                searchTerm={searchTerm}
              />
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'node_value',
      headerName: 'Value',
      width: 300,
      resizable: true,
      flex: 1,
      editable: true,
      renderCell: (params: GridCellParams) => {
        const row = params.row as DatabaseRow;
        const isExpandable = row.node_type === 'object' || row.node_type === 'array';
        const isEditable = !isExpandable; // Only primitive/value types are editable

        if (isExpandable) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {getDisplayValue(row, rows)}
            </Typography>
          );
        }

        return (
          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              backgroundColor: isEditable ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
              border: isEditable ? '1px solid rgba(255, 193, 7, 0.3)' : 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              '&:hover': isEditable ? {
                backgroundColor: 'rgba(255, 235, 59, 0.2)',
                border: '1px solid rgba(255, 193, 7, 0.5)',
              } : {}
            }}
          >
            <HighlightText
              text={String(row.node_value)}
              searchTerm={searchTerm}
            />
          </Typography>
        );
      },
      renderEditCell: (params) => {
        const row = params.row as DatabaseRow;

        if (row.node_type === 'primitive' && typeof row.node_value === 'boolean') {
          return (
            <Select
              value={String(params.value)}
              onChange={(e) => params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value === 'true'
              })}
              size="small"
              fullWidth
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </Select>
          );
        }

        return (
          <TextField
            value={params.value || ''}
            onChange={(e) => params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value
            })}
            size="small"
            fullWidth
            multiline={String(params.value || '').length > 50}
            maxRows={4}
          />
        );
      }
    },
    {
      field: 'node_type',
      headerName: 'Type',
      width: 120,
      resizable: true,
      renderCell: (params: GridCellParams) => {
        const row = params.row as DatabaseRow;
        const colors = {
          object: 'primary',
          array: 'secondary',
          primitive: 'default',
          value: 'default'
        } as const;

        return (
          <Chip
            label={row.node_type}
            size="small"
            color={colors[row.node_type] || 'default'}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'depth',
      headerName: 'Level',
      width: 80,
      resizable: true,
      renderCell: (params: GridCellParams) => (
        <Typography variant="caption" color="text.secondary">
          {params.row.depth}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      resizable: false,
      sortable: false,
      disableColumnMenu: true,
      disableClickEventBubbling: true,
      renderCell: (params: GridCellParams) => {
        const row = params.row as DatabaseRow;
        const siblings = rows.filter(r => r.parent_path === row.parent_path);
        const sortedSiblings = siblings.sort((a, b) => a.display_order - b.display_order);
        const currentIndex = sortedSiblings.findIndex(r => r.id === row.id);


        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Delete Row">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRow(row);
                }}
                color="error"
                sx={{ p: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Sibling">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddSibling(row);
                }}
                color="info"
                sx={{ p: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Child">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddChild(row);
                }}
                color="primary"
                sx={{ p: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ], [handleToggleExpand, rows, expandedPaths, handleDeleteRow, handleAddSibling, handleAddChild]);

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading configuration from database...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Header Controls */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6">Configuration: {configId}</Typography>
        <Chip
          label={`${visibleRows.length} visible / ${rows.length} total`}
          size="small"
          variant="outlined"
        />
        {pendingChanges.size > 0 && (
          <Chip
            label={`${pendingChanges.size} unsaved changes`}
            size="small"
            color="warning"
          />
        )}

        {/* Perspective filter — hides rows whose nearest enclosing
            tab / leftNavSection / view doesn't match. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Perspective:
          </Typography>
          <RadioGroup
            row
            value={perspectiveFilter}
            onChange={(e) => setPerspectiveFilter(e.target.value as typeof perspectiveFilter)}
            sx={{ '& .MuiFormControlLabel-root': { mr: 0.5 } }}
          >
            <FormControlLabel value="all"        control={<Radio size="small" />} label={<Typography variant="caption">All</Typography>} />
            <FormControlLabel value="top-down"   control={<Radio size="small" />} label={<Typography variant="caption">Top-Down</Typography>} />
            <FormControlLabel value="bottom-up"  control={<Radio size="small" />} label={<Typography variant="caption">Bottom-Up</Typography>} />
            <FormControlLabel value="common"     control={<Radio size="small" />} label={<Typography variant="caption">Common</Typography>} />
          </RadioGroup>
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Search Box and Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search: model | key:model | value:text | exact:model (Press Enter or click Search)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />,
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveSearchTerm('');
                      // Clear search results and remove search expansions
                      setSearchResults([]);
                      setExpandedPaths(prev => new Set([...prev].filter(p => !searchExpandedPaths.has(p))));
                      setSearchExpandedPaths(new Set());
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{ width: 400 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<SearchIcon />}
              onClick={executeSearch}
              disabled={!searchTerm.trim()}
            >
              Search
            </Button>
            {searchResults.length > 0 && (
              <Chip
                label={`${searchResults.length} matches`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <Tooltip title="Collapse All">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CollapseAllIcon />}
                onClick={handleCollapseAll}
                disabled={loading || expandedPaths.size === 0}
              >
                Collapse All
              </Button>
            </Tooltip>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={saveChanges}
            disabled={loading || pendingChanges.size === 0}
          >
            Save Changes
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowPreview(true)}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={runValidation}
          >
            Validate Config
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={exportAsJson}
          >
            Export JSON
          </Button>
          {ociAvailable && (
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSyncToOCI}
              disabled={loading || ociSyncing}
            >
              {ociSyncing ? 'Syncing...' : 'Sync to OCI'}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            color="secondary"
            onClick={handleSyncNavigation}
            disabled={loading || navSyncing}
          >
            {navSyncing ? 'Syncing...' : 'Sync Navigation'}
          </Button>
        </Box>
      </Box>

      {/* Order Mismatch Warning */}
      {orderMismatch && (
        <Paper elevation={1} sx={{ mb: 2, p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Order Mismatch Detected
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SyncIcon />}
                onClick={handleSyncOrder}
                disabled={syncInProgress}
                color="primary"
              >
                Sync to JSON
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UndoIcon />}
                onClick={handleRevertOrder}
                disabled={syncInProgress}
                color="primary"
              >
                Revert to JSON
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            The visual order of items differs from the JSON structure. Use "Sync to JSON" to update the JSON order to match the visual order, or "Revert to JSON" to restore the visual order from the JSON.
          </Typography>
        </Paper>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Box sx={{ height: '60vh', minHeight: '400px' }}>
        <div style={{ height: '60vh', width: '100%', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
              <tr>
                <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'left', minWidth: '400px' }}>Path</th>
                <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'left', minWidth: '200px' }}>Value</th>
                <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'left', minWidth: '100px' }}>Type</th>
                <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'left', minWidth: '80px' }}>Level</th>
                <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'left', minWidth: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const isExpandable = row.node_type === 'object' || row.node_type === 'array';
                const isEditable = !isExpandable;
                const isMatch = searchResults.includes(row.id);

                const isUnsaved = row.id < 0;

                // Compute this row's position among its siblings to drive Move Up/Down disabled state
                const allCurrentRowsForSort = [...rows, ...Array.from(pendingChanges.values()).filter(r => r.id < 0)];
                const siblingsSorted = allCurrentRowsForSort
                  .filter(r => r.parent_path === row.parent_path)
                  .sort((a, b) => a.path_order.localeCompare(b.path_order));
                const siblingIdx = siblingsSorted.findIndex(s => s.id === row.id);
                const isFirstSibling = siblingIdx <= 0;
                const isLastSibling = siblingIdx === siblingsSorted.length - 1;

                return (
                  <tr
                    key={row.id}
                    style={{
                      backgroundColor: isUnsaved ? 'rgba(144, 202, 249, 0.15)' : // Light blue for unsaved
                        isMatch ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
                      cursor: isExpandable ? 'pointer' : 'default',
                      borderLeft: isUnsaved ? '3px solid #42a5f5' : 'none' // Blue border for unsaved
                    }}
                    onClick={isExpandable ? () => handleToggleExpand(row.json_path) : undefined}
                  >
                    <td style={{
                      border: '1px solid #e0e0e0',
                      padding: '8px',
                      paddingLeft: `${8 + (row.depth * 20)}px`,
                      backgroundColor: isEditable ? 'rgba(255, 235, 59, 0.05)' : 'transparent'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isExpandable && (
                          <span style={{ width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {row.isExpanded ? '▼' : '▶'}
                          </span>
                        )}
                        {!isExpandable && <span style={{ width: '16px' }}></span>}
                        {row.id < 0 && !/\[\d+\]$/.test(row.json_path) ? (
                          // Editable key for manually added new rows that
                          // sit under OBJECTS (not arrays — array elements
                          // are keyed by [N] and aren't renamable).
                          // Previously this checked `.startsWith('newItem')`
                          // which caused the input to unmount the moment
                          // the user typed a character that left the
                          // `newItem*` prefix behind, killing focus.
                          <input
                            type="text"
                            value={row.json_path.split('.').pop() || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleKeyChange(row, e.target.value);
                            }}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              border: '1px solid #ccc',
                              background: '#fff3e0',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              width: '100px'
                            }}
                            placeholder="key name"
                          />
                        ) : (
                          // Read-only for existing rows and hierarchically cloned siblings
                          <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                            <HighlightText
                              text={getFriendlyPathLabel(row, rows)}
                              searchTerm={activeSearchTerm}
                            />
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{
                      border: '1px solid #e0e0e0',
                      padding: '8px',
                      backgroundColor: isEditable ? 'rgba(255, 235, 59, 0.05)' : 'transparent'
                    }}>
                      {isEditable ? (
                        <input
                          type="text"
                          value={String(row.node_value || '')}
                          onChange={(e) => {
                            // Handle inline editing
                            const newValue = e.target.value;
                            handleCellValueChange({ ...row, node_value: newValue }, row);
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            width: '100%',
                            fontFamily: 'monospace',
                            fontSize: '13px'
                          }}
                        />
                      ) : (
                        <span style={{
                          fontStyle: 'italic',
                          color: '#666',
                          fontFamily: 'monospace',
                          fontSize: '13px'
                        }}>
                          <HighlightText
                            text={getDisplayValue(row, rows)}
                            searchTerm={activeSearchTerm}
                          />
                        </span>
                      )}
                    </td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px' }}>
                      {row.id < 0 ? (
                        // New row — let the user pick primitive vs object
                        // vs array before saving. After save the type is
                        // fixed (changing an existing row's type would
                        // require rewriting its descendants).
                        <select
                          value={row.node_type}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleNodeTypeChange(row, e.target.value as DatabaseRow['node_type']);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: '11px',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            border: '1px solid #90caf9',
                            background: '#fff3e0',
                            fontFamily: 'monospace',
                          }}
                        >
                          <option value="primitive">primitive</option>
                          <option value="object">object</option>
                          <option value="array">array</option>
                        </select>
                      ) : (
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          backgroundColor: row.node_type === 'object' ? '#e3f2fd' :
                            row.node_type === 'array' ? '#fff3e0' : '#e8f5e8',
                          color: row.node_type === 'object' ? '#1976d2' :
                            row.node_type === 'array' ? '#f57c00' : '#388e3c'
                        }}>
                          {row.node_type}
                        </span>
                      )}
                    </td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                      {row.depth}
                    </td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          title="Move Up"
                          disabled={isFirstSibling}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveUp(row);
                          }}
                          style={{
                            border: 'none',
                            background: isFirstSibling ? '#BDBDBD' : '#607d8b',
                            color: 'white',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            cursor: isFirstSibling ? 'not-allowed' : 'pointer',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <ArrowUpwardIcon style={{ fontSize: 14 }} />
                        </button>
                        <button
                          title="Move Down"
                          disabled={isLastSibling}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveDown(row);
                          }}
                          style={{
                            border: 'none',
                            background: isLastSibling ? '#BDBDBD' : '#607d8b',
                            color: 'white',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            cursor: isLastSibling ? 'not-allowed' : 'pointer',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <ArrowDownwardIcon style={{ fontSize: 14 }} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRow(row);
                          }}
                          style={{
                            border: 'none',
                            background: '#ff5722',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          Delete
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddSibling(row);
                          }}
                          style={{
                            border: 'none',
                            background: '#2196f3',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            marginRight: '4px'
                          }}
                        >
                          Add Sibling
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddChild(row);
                          }}
                          style={{
                            border: 'none',
                            background: '#4caf50',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          Add Child
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Box>


      {/* Preview dialog — formerly the always-visible Config Preview block.
          Opens from the "Preview" toolbar button. */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Config Preview
          {pendingChanges.size > 0 && (
            <Chip
              label="Live (includes unsaved changes)"
              size="small"
              color="warning"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'auto',
              backgroundColor: '#f8f9fa',
              p: 2,
              height: '70vh',
            }}
          >
            <pre style={{
              margin: 0,
              fontFamily: 'Monaco, Consolas, monospace',
              fontSize: '12px',
              lineHeight: 1.4,
              color: '#333',
              whiteSpace: 'pre-wrap',
            }}>
              {JSON.stringify(currentJson, null, 2)}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation results dialog */}
      <Dialog
        open={showValidation}
        onClose={() => setShowValidation(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Config Validation
        </DialogTitle>
        <DialogContent dividers>
          {validationIssues.length === 0 ? (
            <Alert severity="success">
              No issues found. The structure is valid, all <code>inPerspectives</code> values are
              <code> 'top-down' </code> or <code> 'bottom-up' </code>, and no view <code>id</code>
              has duplicate perspectives.
            </Alert>
          ) : (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationIssues.length} {validationIssues.length === 1 ? 'issue' : 'issues'} found.
              </Alert>
              <Box
                sx={{
                  fontFamily: 'Monaco, Consolas, monospace',
                  fontSize: '12px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  p: 2,
                  backgroundColor: '#fff5f5',
                  border: '1px solid #ffcdd2',
                  borderRadius: 1,
                  maxHeight: '60vh',
                  overflow: 'auto',
                }}
              >
                {validationIssues.map((msg, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    {i + 1}. {msg}
                  </div>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowValidation(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Modal */}
      <Dialog
        open={showTemplateModal}
        onClose={handleCloseTemplateModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Template Type for Missing Views
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following views are missing from the database. Please select a template type for each view:
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>View Name</strong></TableCell>
                  <TableCell><strong>Template Type</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingViews.map((viewName) => (
                  <TableRow key={viewName}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {viewName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={templateSelections[viewName] || 'grid'}
                          onChange={(e) => handleTemplateChange(viewName, e.target.value)}
                        >
                          <MenuItem value="grid">Grid</MenuItem>
                          <MenuItem value="groupBy">GroupBy</MenuItem>
                          <MenuItem value="sort">Sort</MenuItem>
                          <MenuItem value="companionView">Companion</MenuItem>
                          <MenuItem value="rollUp">RollUp</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateModal} disabled={creatingViews}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateViews}
            variant="contained"
            color="primary"
            disabled={creatingViews}
          >
            {creatingViews ? 'Creating...' : 'Create Views'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseUIConfigGrid;